import { useState, useRef, useEffect, useCallback } from 'react';
import { useInboxStore, type Message, type Conversation, type Attachment } from '../../stores/inbox-store';
import { ConfidenceBadge } from './ConfidenceBadge';
import { PhoneNumber } from '../phone/PhoneNumber';
import type { Brand } from '../../lib/telephony';
import clsx from 'clsx';
import {
  INBOX_ANTWOORD_MAX_TEKENS,
  bereidInboxAntwoordParameters,
  eersteNaamMetVoornaam,
  isGerenderdInboxAntwoord,
  renderInboxAntwoord,
  saneerTemplateParameter,
  voornaamVoorTemplate,
} from '../../lib/inbox/whatsapp-template-antwoord';

/** Wat kan er nog buiten het 24-uursvenster (GET /api/inbox/reply-per-email). */
interface TerugvalOpties {
  emailAdres: string | null;
  naam: string | null;
  templateBeschikbaar: boolean;
  templateReden: string | null;
  vensterOpen: boolean;
  sourceMessageId: string | null;
}

function formatPhoneDisplay(phone: string): string {
  if (!phone) return '';
  const cleaned = phone.replace(/[^\d+]/g, '');
  if (cleaned.startsWith('+31') && cleaned.length === 12) {
    const local = '0' + cleaned.slice(3);
    return local.slice(0, 2) + '-' + local.slice(2);
  }
  return cleaned;
}

function formatTime(dateStr: string): string {
  return new Date(dateStr).toLocaleTimeString('nl-NL', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatDateSeparator(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));

  if (days === 0) return 'Vandaag';
  if (days === 1) return 'Gisteren';
  return date.toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' });
}

function shouldShowDateSeparator(messages: Message[], index: number): boolean {
  if (index === 0) return true;
  const current = new Date(messages[index]!.message_date).toDateString();
  const previous = new Date(messages[index - 1]!.message_date).toDateString();
  return current !== previous;
}

function useAuthToken() {
  const [token, setToken] = useState<string | null>(null);
  useEffect(() => {
    import('../../lib/supabase').then(({ supabase }) => {
      supabase.auth.getSession().then(({ data }) => {
        setToken(data.session?.access_token ?? null);
      });
    });
  }, []);
  return token;
}

interface WhatsAppChatProps {
  conversation: Conversation;
  messages: Message[];
  attachments: Attachment[];
  conceptBlokkade?: string | null;
  afhandelGeblokkeerd?: boolean;
  onAfhandelen?: () => Promise<void>;
}

export function WhatsAppChat({ conversation, messages, attachments, conceptBlokkade,
  afhandelGeblokkeerd = false, onAfhandelen }: WhatsAppChatProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [replyText, setReplyText] = useState('');
  const [extraContext, setExtraContext] = useState('');
  const [showExtraContext, setShowExtraContext] = useState(false);
  const [justSent, setJustSent] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const token = useAuthToken();
  // Buiten het 24-uursvenster (plan 03-11): template of e-mail.
  const [terugval, setTerugval] = useState<TerugvalOpties | null>(null);
  const [emailBezig, setEmailBezig] = useState(false);
  const [emailFout, setEmailFout] = useState<string | null>(null);
  const [emailVerstuurdNaar, setEmailVerstuurdNaar] = useState<string | null>(null);

  // Build a lookup: message_id → image attachments
  const imagesByMessage = new Map<string, Attachment[]>();
  for (const att of attachments) {
    if (att.mime_type.startsWith('image/')) {
      const existing = imagesByMessage.get(att.message_id) || [];
      existing.push(att);
      imagesByMessage.set(att.message_id, existing);
    }
  }

  const {
    aiDraft,
    aiDraftAnalysis,
    aiDraftConfidence,
    aiDraftConfidenceReason,
    aiDraftModel,
    aiDraftError,
    isGeneratingDraft,
    isSending,
    sendError,
    sendReply,
    generateDraft,
    updateStatus,
    lezerModus,
  } = useInboxStore();

  // V3 (lezerModus): het concept staat direct in het antwoordveld, net als bij e-mail.
  useEffect(() => {
    if (lezerModus && aiDraft) setReplyText(aiDraft);
  }, [lezerModus, aiDraft]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Auto-resize textarea
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.max(48, Math.min(el.scrollHeight, 160)) + 'px';
  }, [replyText]);

  const handleSend = async () => {
    if (!replyText.trim() || isSending) return;
    await sendReply('', replyText, { handmatig_antwoord: true }); // No HTML for WhatsApp
    if (useInboxStore.getState().sendError) return;
    setReplyText('');
    setJustSent(true);
  };

  const handleUseDraft = () => {
    if (aiDraft) setReplyText(aiDraft);
  };

  const handleGenerateDraft = () => {
    generateDraft(extraContext.trim() || undefined);
  };

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      handleSend();
    }
  }, [replyText, isSending]);

  const handleMarkDone = () => {
    if (afhandelGeblokkeerd) return;
    if (onAfhandelen) { void onAfhandelen(); return; }
    updateStatus(conversation.id, 'afgehandeld');
    setJustSent(false);
  };

  // Sneltoets D = draft in composer laden + focus (zelfde als ThreadDetail).
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT') return;
      if (target.closest('[data-conv-item]')) return;
      if (e.key === 'd' && !e.metaKey && !e.ctrlKey && !e.altKey && aiDraft) {
        e.preventDefault();
        setReplyText(aiDraft);
        textareaRef.current?.focus();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [aiDraft]);

  // 24-hour window check
  const lastIncoming = [...messages].reverse().find(m => m.is_incoming);
  const windowExpired = lastIncoming
    ? (Date.now() - new Date(lastIncoming.message_date).getTime()) > 24 * 60 * 60 * 1000
    : false;

  // Venster dicht: vraag de server wat er nog kan (template in de registry
  // voor dit merk, geverifieerd e-mailadres uit de poort). Eén call per gesprek.
  useEffect(() => {
    setTerugval(null);
    setEmailFout(null);
    setEmailVerstuurdNaar(null);
    if (!windowExpired || !token) return;
    let actief = true;
    fetch(`/api/inbox/reply-per-email?conversation_id=${encodeURIComponent(conversation.id)}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (r) => (r.ok ? (await r.json()) as TerugvalOpties : null))
      .then((data) => { if (actief && data) setTerugval(data); })
      .catch(() => { /* zonder opties blijft alleen de uitleg staan */ });
    return () => { actief = false; };
  }, [windowExpired, token, conversation.id, lastIncoming?.id]);

  const templateVoornaam = voornaamVoorTemplate(eersteNaamMetVoornaam(terugval?.naam, conversation.from_name));
  const templateParameters = windowExpired ? bereidInboxAntwoordParameters({ voornaam: templateVoornaam, antwoord: replyText }) : null;
  const templateTekstLengte = windowExpired ? saneerTemplateParameter(replyText).length : 0;
  const templateTeLang = templateTekstLengte > INBOX_ANTWOORD_MAX_TEKENS;

  const handleSendEmail = async () => {
    if (!replyText.trim() || emailBezig || isSending || !token || !terugval?.emailAdres) return;
    setEmailBezig(true);
    setEmailFout(null);
    try {
      const r = await fetch('/api/inbox/reply-per-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ conversation_id: conversation.id, body_text: replyText, source_message_id: lastIncoming?.id ?? null }),
      });
      const data = await r.json().catch(() => ({})) as { error?: string; email?: string; warning?: string };
      if (!r.ok) { setEmailFout(data.error || 'Versturen per e-mail mislukt.'); return; }
      setEmailVerstuurdNaar(data.email || terugval.emailAdres);
      if (data.warning) setEmailFout(data.warning);
      setReplyText('');
      setJustSent(true);
      // Thread opnieuw laden zodat de notitie "[Per e-mail beantwoord …]" verschijnt.
      void useInboxStore.getState().setActiveThread(conversation.id);
    } catch (e) {
      setEmailFout(e instanceof Error ? e.message : 'Versturen per e-mail mislukt.');
    } finally {
      setEmailBezig(false);
    }
  };

  return (
    <div className="flex-1 flex flex-col min-w-0 min-h-0 overflow-hidden">
      {/* Chat messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4 bg-[#f0f2f5]">
        {/* Phone number header */}
        <div className="text-center mb-4">
          <span className="inline-flex items-center gap-1.5 bg-white/80 text-xs text-slate-500 px-3 py-1.5 rounded-full">
            <svg className="w-3.5 h-3.5 text-emerald-500" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            <PhoneNumber number={conversation.phone} display={formatPhoneDisplay(conversation.phone || '')} brand={conversation.brand as Brand} sourceContext="whatsapp-header" />
          </span>
        </div>

        {messages.map((msg, idx) => (
          <div key={msg.id}>
            {/* Date separator */}
            {shouldShowDateSeparator(messages, idx) && (
              <div className="text-center my-3">
                <span className="bg-white/80 text-xs text-slate-500 px-3 py-1 rounded-full">
                  {formatDateSeparator(msg.message_date)}
                </span>
              </div>
            )}

            {/* Profile name above first incoming message in a group */}
            {msg.is_incoming && (idx === 0 || !messages[idx - 1]?.is_incoming) && msg.from_name && (
              <p className="text-xs text-slate-500 ml-2 mb-0.5">{msg.from_name}</p>
            )}

            {/* Chat bubble */}
            <div className={clsx(
              'flex mb-1',
              msg.is_incoming ? 'justify-start' : 'justify-end'
            )}>
              <div className={clsx(
                'max-w-[75%] rounded-lg shadow-sm overflow-hidden',
                msg.is_incoming
                  ? 'bg-white text-slate-800'
                  : 'bg-[#d9fdd3] text-slate-800'
              )}>
                {/* Image attachments */}
                {token && imagesByMessage.get(msg.id)?.map(att => {
                  const imgUrl = `/api/inbox/whatsapp-media?id=${att.id}&token=${token}`;
                  return (
                    <button
                      key={att.id}
                      onClick={() => setLightboxUrl(imgUrl)}
                      className="block w-full cursor-pointer"
                    >
                      <img
                        src={imgUrl}
                        alt="WhatsApp afbeelding"
                        className="w-full max-w-[320px] rounded-t-lg object-cover"
                        loading="lazy"
                      />
                    </button>
                  );
                })}
                <div className="px-3 py-2">
                  {/* Only show text if it's not just the placeholder */}
                  {msg.body_text && !msg.body_text.match(/^\[(image|video|audio|sticker|document) ontvangen\]$/) && (
                    <p className="text-sm whitespace-pre-wrap break-words">
                      {msg.body_text}
                    </p>
                  )}
                  {/* Show placeholder only if no images loaded */}
                  {!imagesByMessage.has(msg.id) && !msg.body_text && (
                    <p className="text-sm text-slate-400 italic">
                      [{msg.channel === 'whatsapp' ? 'Media' : 'Bericht'}]
                    </p>
                  )}
                  <p className={clsx(
                    'text-[10px] mt-1 text-right',
                    msg.is_incoming ? 'text-slate-400' : 'text-emerald-600/60'
                  )}>
                    {!msg.is_incoming && msg.channel === 'email' && (
                      <span className="mr-2 rounded bg-sky-100 px-1 py-px text-sky-700" title="Antwoord ging per e-mail omdat het WhatsApp-venster dicht was">per e-mail</span>
                    )}
                    {!msg.is_incoming && msg.channel !== 'email' && isGerenderdInboxAntwoord(msg.body_text) && (
                      <span className="mr-2 rounded bg-emerald-100 px-1 py-px text-emerald-700" title="Verstuurd als WhatsApp-template buiten het 24-uursvenster">template</span>
                    )}
                    {formatTime(msg.message_date)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* 24h window: template of e-mail (plan 03-11) */}
      {windowExpired && (
        <div className="px-4 py-2 bg-amber-50 border-t border-amber-200" data-testid="venster-dicht">
          <p className="text-xs text-amber-800">
            Het 24-uursvenster is dicht. Je antwoord gaat als WhatsApp-template (vaste kop, jouw tekst in één alinea) of per e-mail.
          </p>
          {terugval && !terugval.templateBeschikbaar && (
            <p className="mt-1 text-xs text-amber-700">Template niet beschikbaar: {terugval.templateReden || 'onbekend'}{terugval.emailAdres ? ' Antwoord per e-mail.' : ''}</p>
          )}
          {terugval && !terugval.emailAdres && (
            <p className="mt-1 text-xs text-amber-700">Geen geverifieerd e-mailadres bij deze klant; e-mail als terugval is niet mogelijk. Bel de klant of zet eerst een adres op de klantkaart.</p>
          )}
        </div>
      )}

      {/* AI Draft section */}
      {!aiDraft && !isGeneratingDraft && !aiDraftError && (messages.some(m => m.is_incoming) || conversation.from_email) && (
        <div className="px-4 py-2 border-t border-slate-200 bg-white flex items-center gap-3">
          <button
            onClick={handleGenerateDraft}
            className="text-sm text-indigo-600 hover:text-indigo-700 flex items-center gap-1.5"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
            </svg>
            AI concept
          </button>
          {!showExtraContext && (
            <button onClick={() => setShowExtraContext(true)} className="text-xs text-slate-400 hover:text-slate-600">
              + context
            </button>
          )}
          {showExtraContext && (
            <input
              type="text"
              value={extraContext}
              onChange={(e) => setExtraContext(e.target.value)}
              placeholder="Extra context..."
              className="flex-1 text-sm border border-slate-200 rounded-lg px-3 py-1.5 text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              onKeyDown={(e) => { if (e.key === 'Enter') handleGenerateDraft(); }}
            />
          )}
        </div>
      )}

      {isGeneratingDraft && (
        <div className="px-4 py-2 border-t border-slate-200 bg-white">
          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 animate-pulse">
            <div className="h-3 bg-indigo-100 rounded w-3/4 mb-2" />
            <div className="h-3 bg-indigo-100 rounded w-1/2" />
          </div>
        </div>
      )}

      {aiDraftError && (
        <div className="px-4 py-2 border-t border-slate-200 bg-white">
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center justify-between">
            <p className="text-sm text-red-700">{aiDraftError}</p>
            <button onClick={handleGenerateDraft} className="text-sm font-medium text-red-600 hover:text-red-700 ml-3 flex-shrink-0">
              Opnieuw
            </button>
          </div>
        </div>
      )}

      {aiDraft && (
        <div className="px-4 py-2 border-t border-slate-200 bg-white">
          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
                </svg>
                <span className="text-sm font-medium text-indigo-700">AI Concept</span>
                <ConfidenceBadge confidence={aiDraftConfidence} reason={aiDraftConfidenceReason} model={aiDraftModel} />
              </div>
              <button
                onClick={handleUseDraft}
                disabled={isSending}
                className="text-sm font-medium text-indigo-600 hover:text-indigo-700 bg-indigo-100 px-3 py-1 rounded-md"
                title={conceptBlokkade || 'Sneltoets: D'}
              >
                Gebruiken
              </button>
            </div>
            {aiDraftAnalysis && (
              <p className="text-xs text-indigo-500 mb-2 italic">AI begrijpt: {aiDraftAnalysis}</p>
            )}
            <p className="text-sm text-slate-700 whitespace-pre-wrap">{aiDraft}</p>
            <div className="mt-2 pt-2 border-t border-indigo-200 flex items-center gap-2">
              <input
                type="text"
                value={extraContext}
                onChange={(e) => setExtraContext(e.target.value)}
                placeholder="Context toevoegen..."
                className="flex-1 text-sm border border-indigo-200 bg-white rounded-lg px-3 py-1 text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                onKeyDown={(e) => { if (e.key === 'Enter') handleGenerateDraft(); }}
              />
              <button
                onClick={handleGenerateDraft}
                className="text-sm font-medium text-indigo-600 hover:text-indigo-700 bg-indigo-100 px-3 py-1 rounded-lg"
              >
                Opnieuw
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reply composer */}
      <div className="px-4 py-3 border-t border-slate-200 bg-white flex-shrink-0">
        {justSent && conversation.status !== 'afgehandeld' && (
          <div className="mb-2 flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
            <svg className="w-4 h-4 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span className="text-sm text-emerald-700 flex-1">Verstuurd</span>
            <button onClick={handleMarkDone} disabled={afhandelGeblokkeerd} className="text-sm font-medium text-emerald-700 hover:text-emerald-800 bg-emerald-100 px-3 py-1 rounded-md">
              Afhandelen
            </button>
            <button onClick={() => setJustSent(false)} className="text-xs text-slate-400 hover:text-slate-600">
              Sluiten
            </button>
          </div>
        )}

        {sendError && (
          <div className="mb-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700">
            {sendError}
          </div>
        )}

        {conceptBlokkade && <p role="status" className="mb-2 text-xs text-amber-700">AI-waarschuwing: {conceptBlokkade} Controleer je antwoord zelf.</p>}
        {emailFout && (
          <div className="mb-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700">{emailFout}</div>
        )}
        {emailVerstuurdNaar && !emailFout && (
          <div className="mb-2 bg-sky-50 border border-sky-200 rounded-lg px-3 py-2 text-sm text-sky-700">Per e-mail verstuurd naar {emailVerstuurdNaar}.</div>
        )}
        <div className="flex items-end gap-2">
          <textarea
            ref={textareaRef}
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={windowExpired ? 'Typ je antwoord (\u00e9\u00e9n alinea; regels worden samengevoegd)...' : 'Typ een bericht...'}
            rows={1}
            style={{ minHeight: '48px', maxHeight: '160px' }}
            className="flex-1 border border-slate-200 rounded-2xl px-4 py-3 text-sm text-slate-700 resize-none focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
          />
          {!windowExpired && (
            <button
              onClick={handleSend}
              disabled={!replyText.trim() || isSending}
              title={conceptBlokkade || undefined}
              className={clsx(
                'w-10 h-10 rounded-full flex items-center justify-center transition-colors flex-shrink-0',
                replyText.trim() && !isSending
                  ? 'bg-emerald-500 text-white hover:bg-emerald-600'
                  : 'bg-slate-100 text-slate-400 cursor-not-allowed'
              )}
            >
              {isSending ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" />
                </svg>
              )}
            </button>
          )}
        </div>
        {windowExpired ? (
          <div className="mt-2 space-y-2" data-testid="venster-dicht-acties">
            {replyText.trim() && templateParameters?.ok && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 px-3 py-2">
                <p className="text-[10px] uppercase tracking-wide text-emerald-700 mb-1">Zo ziet de klant het (template)</p>
                <p className="text-xs text-slate-700 whitespace-pre-wrap">{renderInboxAntwoord(templateParameters.voornaam, templateParameters.antwoord, conversation.brand)}</p>
              </div>
            )}
            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={handleSend}
                disabled={!replyText.trim() || isSending || emailBezig || templateTeLang || terugval?.templateBeschikbaar === false}
                title={terugval?.templateBeschikbaar === false ? (terugval.templateReden || 'Template niet beschikbaar') : templateTeLang ? `Kort het antwoord in tot ${INBOX_ANTWOORD_MAX_TEKENS} tekens.` : 'Vaste kop + jouw tekst als WhatsApp-template'}
                className={clsx(
                  'text-sm font-medium px-3 py-1.5 rounded-lg transition-colors',
                  replyText.trim() && !isSending && !emailBezig && !templateTeLang && terugval?.templateBeschikbaar !== false
                    ? 'bg-emerald-500 text-white hover:bg-emerald-600'
                    : 'bg-slate-100 text-slate-400 cursor-not-allowed'
                )}
              >
                {isSending ? 'Versturen\u2026' : 'Verstuur als template'}
              </button>
              <button
                onClick={handleSendEmail}
                disabled={!replyText.trim() || isSending || emailBezig || !terugval?.emailAdres}
                title={terugval?.emailAdres ? `Per e-mail naar ${terugval.emailAdres}, met een notitie op dit gesprek` : 'Geen geverifieerd e-mailadres bij deze klant'}
                className={clsx(
                  'text-sm font-medium px-3 py-1.5 rounded-lg border transition-colors',
                  replyText.trim() && !isSending && !emailBezig && terugval?.emailAdres
                    ? 'border-sky-300 text-sky-700 bg-white hover:bg-sky-50'
                    : 'border-slate-200 text-slate-400 bg-slate-50 cursor-not-allowed'
                )}
              >
                {emailBezig ? 'Versturen\u2026' : terugval?.emailAdres ? `Antwoord per e-mail naar ${terugval.emailAdres}` : 'Antwoord per e-mail'}
              </button>
              <span className={clsx('ml-auto text-[10px]', templateTeLang ? 'text-red-600 font-medium' : 'text-slate-400')}>
                {templateTekstLengte}/{INBOX_ANTWOORD_MAX_TEKENS}
              </span>
            </div>
          </div>
        ) : (
          <div className="mt-1 text-[10px] text-slate-400">
            {'\u2318'}+Enter om te versturen
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightboxUrl && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-8"
          onClick={() => setLightboxUrl(null)}
        >
          <div className="relative max-w-full max-h-full" onClick={e => e.stopPropagation()}>
            <img
              src={lightboxUrl}
              alt="WhatsApp afbeelding"
              className="max-w-full max-h-[85vh] rounded-lg shadow-2xl"
            />
            <div className="absolute top-3 right-3 flex gap-2">
              <a
                href={lightboxUrl}
                download
                className="bg-white/90 hover:bg-white rounded-full p-2 shadow-lg transition-colors"
                title="Downloaden"
                onClick={e => e.stopPropagation()}
              >
                <svg className="w-5 h-5 text-slate-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                </svg>
              </a>
              <button
                onClick={() => setLightboxUrl(null)}
                className="bg-white/90 hover:bg-white rounded-full p-2 shadow-lg transition-colors"
                title="Sluiten"
              >
                <svg className="w-5 h-5 text-slate-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
